import React, {Suspense} from 'react'
import {lazyWithRetry} from '@/helpers/lazyWithRetry'
import {
  createBrowserRouter,
  LoaderFunctionArgs,
  redirect,
} from 'react-router-dom'
import MainLayout from '@/layouts/MainLayout'
import {store} from '@/store'
import {getUser} from '@/store/actions'
import OnlyDesktop from '@/pages/OnlyDesktop'
import i18n from 'i18next'
import {SET_LANGUAGE} from '@/store/slices'
import {SET_FULL_ACCOUNT} from '@/store/slices/storage'

const Map = lazyWithRetry(() => import('@/pages/Map'))
const Auth = lazyWithRetry(() => import('@/pages/Auth'))
const PrivacyPolicy = lazyWithRetry(() => import('@/pages/PrivacyPolicy'))
const TermsAndConditions = lazyWithRetry(() => import('@/pages/TermsAndConditions'))
const Inventory = lazyWithRetry(() => import('@/components/Inventory'))
const HexaTrading = lazyWithRetry(() => import('@/components/HexaTrading'))
const Store = lazyWithRetry(() => import('@/components/Store'))
const MarketMakerCabinet = lazyWithRetry(() => import('@/components/MarketMakerCabinet'))
const Extras = lazyWithRetry(() => import('@/components/Extras'))
const MapOfHeat = lazyWithRetry(() => import('@/components/MapOfHeat'))
const Error = lazyWithRetry(() => import('@/pages/error/index'))
const Maintenance = lazyWithRetry(() => import('@/pages/Maintenance'))
const Leaderboard = lazyWithRetry(() => import('@/pages/Leaderboard'))
const Marketplace = lazyWithRetry(() => import('@/components/Store/Marketplace'))
const StoreForLoi = lazyWithRetry(() => import('@/components/Store/StoreForLoi'))
const Sandbox = lazyWithRetry(() => import('@/components/Map'))

const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
      background: 'black',
    }}></div>
)

const LazyRoute: React.FC<{children: React.ReactNode}> = ({children}) => (
  <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
)

const checkOnlyDesktop = async ({
  request,
}: LoaderFunctionArgs): Promise<ReturnType<typeof redirect> | null> => {
  const isMobileDevice =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )

  if (window.innerWidth <= 900 || isMobileDevice) {
    return redirect('/only-desktop')
  }

  const url = new URL(request.url)
  const supporterToken = url.searchParams.get('supporter_token')
  const supporterRefreshToken = url.searchParams.get('supporter_refresh_token')

  if (supporterToken && supporterRefreshToken) {
    const isValidTokenFormat = (token: string): boolean => {
      return token.length > 10 && token.trim().length > 0
    }

    if (
      isValidTokenFormat(supporterToken) &&
      isValidTokenFormat(supporterRefreshToken)
    ) {
      await store.dispatch(
        SET_FULL_ACCOUNT({
          refreshToken: supporterRefreshToken,
          jwt: supporterToken,
          account: 'support',
        })
      )
    } else if (process.env.NODE_ENV === 'development') {
      console.warn('Invalid token format in URL parameters')
    }
  }

  if (store.getState().storage.account) {
    await checkUserAndLanguage()
  }

  return null
}

const checkUserAndLanguage = async (): Promise<boolean> => {
  if (store.getState().storage.account && !store.getState().index.user) {
    const response = await store.dispatch(getUser())
    if (!response.payload) {
      return false
    }
  }

  const storageLang = store.getState().storage.language
  const currentLang = store.getState().index.language
  if (storageLang && storageLang !== currentLang) {
    await store.dispatch(SET_LANGUAGE(storageLang))
    i18n.changeLanguage(storageLang)
  }

  return true
}

const redirectWithoutAuth = async (): Promise<ReturnType<
  typeof redirect
> | null> => {
  if (!store.getState().storage.account) {
    return redirect('/')
  }

  const success = await checkUserAndLanguage()
  if (!success) {
    return redirect('/')
  }

  return null
}

const UserLoader = async (): Promise<null> => {
  await checkUserAndLanguage()
  return null
}

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <LazyRoute>
        <Auth />
      </LazyRoute>
    ),
    loader: checkOnlyDesktop,
  },
  {
    element: <MainLayout />,
    loader: checkOnlyDesktop,
    children: [
      {
        path: '/main',
        loader: UserLoader,
        element: (
          <LazyRoute>
            <Map />
          </LazyRoute>
        ),
      },
      {
        path: '/analytics',
        element: (
          <LazyRoute>
            <MapOfHeat />
          </LazyRoute>
        ),
        loader: redirectWithoutAuth,
      },
      {
        path: '/trading',
        element: (
          <LazyRoute>
            <HexaTrading />
          </LazyRoute>
        ),
        loader: redirectWithoutAuth,
      },
      {
        path: '/inventory',
        element: (
          <LazyRoute>
            <Inventory />
          </LazyRoute>
        ),
        loader: redirectWithoutAuth,
      },
      {
        path: '/store',
        element: (
          <LazyRoute>
            <Store />
          </LazyRoute>
        ),
        loader: redirectWithoutAuth,
      },
      {
        path: '/mm',
        element: (
          <LazyRoute>
            <MarketMakerCabinet />
          </LazyRoute>
        ),
        loader: redirectWithoutAuth,
      },
      {
        path: '/extras',
        element: (
          <LazyRoute>
            <Extras />
          </LazyRoute>
        ),
        loader: redirectWithoutAuth,
      },
      {
        path: '/marketplace',
        loader: UserLoader,
        element: (
          <LazyRoute>
            <Marketplace />
          </LazyRoute>
        ),
      },
      {
        path: '/store-loi',
        loader: UserLoader,
        element: (
          <LazyRoute>
            <StoreForLoi />
          </LazyRoute>
        ),
      },
      {
        path: '/sandbox',
        element: (
          <LazyRoute>
            <Sandbox />
          </LazyRoute>
        ),
        loader: UserLoader,
      },
    ],
  },
  {
    path: '/leaderboard',
    loader: UserLoader,
    element: (
      <LazyRoute>
        <Leaderboard />
      </LazyRoute>
    ),
    index: false,
  },
  {
    path: '/only-desktop',
    element: <OnlyDesktop />,
    index: false,
  },
  {
    path: '/privacy-policy',
    element: (
      <LazyRoute>
        <PrivacyPolicy />
      </LazyRoute>
    ),
  },
  {
    path: '/terms-and-conditions',
    element: (
      <LazyRoute>
        <TermsAndConditions />
      </LazyRoute>
    ),
  },
  {
    path: '/maintenance',
    element: (
      <LazyRoute>
        <Maintenance />
      </LazyRoute>
    ),
  },
  {
    path: '*',
    element: (
      <LazyRoute>
        <Error />
      </LazyRoute>
    ),
  },
])

export default router
